import type { LogLevelDesc } from 'loglevel';
import log from 'loglevel';

import type { BannerPosition, BannerVisibilityToken } from 'models/banner';

log.setLevel((process.env.NEXT_PUBLIC_LOG_LEVEL as LogLevelDesc) || 'warn');

const getArrayFromCSV = (csv: string): string[] =>
    csv
        .split(',')
        .map((val) => val.trim())
        .filter((val) => !!val);

export type KALSHI_ENVIRONMENT = 'dev' | 'staging' | 'lx' | 'lxtest' | 'production' | 'demo';

type FACEBOOK_SHARE_METHOD = 'share' | 'send';

interface MaintenanceConfig {
    start_date_time: string;
    end_date_time: string;
    number_of_previous_notice_hours: number;
}

export interface DayTradingHours {
    open_time: string; // in 24hr E.T. time with seconds resolution (e.g. 09:00:00)
    close_time: string; // in 24hr E.T. time with seconds resolution (e.g. 19:00:00)
}

export interface TradingHoursConfig {
    Sunday: DayTradingHours[];
    Monday: DayTradingHours[];
    Tuesday: DayTradingHours[];
    Wednesday: DayTradingHours[];
    Thursday: DayTradingHours[];
    Friday: DayTradingHours[];
    Saturday: DayTradingHours[];
}

interface Config {
    frontendEnv: KALSHI_ENVIRONMENT;
    backendEnv: KALSHI_ENVIRONMENT;
    baseUrl: string;
    exchangeApiUrl: string;
    geoComplyBrowserGuardUrl: string;
    geoComplyEnabled: boolean;
    graphqlUrl: string;
    statsApiUrl: string;
    exchangeWsUrl: string;
    marginWsUrl: string;
    kalshiPublicDocsS3BucketUrl: string;
    kalshiPublicDocsCloudfrontUrl: string;
    kalshiImagesCloudfrontUrl: string;
    kalshiPublicImagesS3BucketUrl: string;
    kalshiProductInfoCloudfrontUrl: string;
    kalshiProfilePreviewImagesS3BucketUrl: string;
    kalshiSocialPublicS3BucketUrl: string;
    helpCenterUrl: string;
    facebookShareMethod: string;
    facebookPixelId: string;
    pollingInterval: number;
    fastPollingInterval: number;
    midPollingInterval: number;
    slowPollingInterval: number;
    tokenExpiration: number;
    extraKycWait: number;
    kycAckWait: number;
    withdrawalFee: number; // in cents
    withdrawalFeeCard: number; // in cents
    maintenanceConfig: MaintenanceConfig;
    tradingHoursConfig: TradingHoursConfig;
    requestTimeoutMs: number;
    commitRef: string;
    sentryDns: string;
    sentrySampleRate: number; // Percentual integer between [0, 100]
    sentryRelease: string;
    sessionLength: number; // Length of the session in seconds
    feSessionLength: number;
    logRocketId: string | undefined;
    manualBannerContent: string;
    manualBannerVisibilityTokens: Array<BannerVisibilityToken>;
    manualBannerPosition: BannerPosition; // 'top' | 'bottom'
    automatedBannerVisibilityTokens: Array<BannerVisibilityToken>;
    socureSdkKey: string;
    socureInternationalSdkKey: string;
    minTimeBetweenRequestsMs: number;
    maxConcurrentRequests: number;
    logRocketSanitizeWords: Array<string>;
    logRocketUnauthenticatedSamplingPercentage: number;
    apiDocsUrl: string;
    enableFixApiKeys: boolean;
    /** Kill-switch for the American odds sports-layout mode. Off unless explicitly enabled. */
    americanOddsEnabled: boolean;
    googleMapsApiKey: string;
    googleOAuthClientId: string;
    appleOAuthClientId: string;
    checkoutPublicKey: string;
    stripePublishableKey: string;
    applePayMerchantId: string;
    sportsRadarApiKey: string;
    sportsRadarSessionReplayId: string;
    sportradarWidgetClientId: string;
    aeropayConsumerId: string;
    aeropayConsumerIdDarkMode: string;
    wolrdCoinAppId: string;
    meshClientId: string;
    braintreeTokenizationKey: string;
    internalApiKey: string;
    statsigEnvironment: string;
    giphyApiKey: string;
    riskifiedDomain: string;
    plaidProtectApiToken: string;
    amplitudeApiKey: string;
    awsWafIntegrationUrl: string;
    awsWafCookieDomainList: string;
    awsWafFetchRetryCount: number;
    awsWafMaxTokenAgeMs: number;
}

const DEFAULT_EXCHANGE_WS_BASE_URL = 'wss://demo-api.kalshi.co';
const STAGING_GEOCOMPLY_BROWSER_GUARD_URL = 'https://stg-cdn.geocomply.com/383/gc-html5.js';
const DEFAULT_GEOCOMPLY_BROWSER_GUARD_URLS: Record<KALSHI_ENVIRONMENT, string> = {
    demo: STAGING_GEOCOMPLY_BROWSER_GUARD_URL,
    dev: STAGING_GEOCOMPLY_BROWSER_GUARD_URL,
    lx: '',
    lxtest: STAGING_GEOCOMPLY_BROWSER_GUARD_URL,
    production: '',
    staging: STAGING_GEOCOMPLY_BROWSER_GUARD_URL,
};
const GEOCOMPLY_BROWSER_GUARD_ORIGINS: Record<KALSHI_ENVIRONMENT, string | null> = {
    demo: 'https://stg-cdn.geocomply.com',
    dev: 'https://stg-cdn.geocomply.com',
    lx: null,
    lxtest: 'https://stg-cdn.geocomply.com',
    production: 'https://cdn.geocomply.com',
    staging: 'https://stg-cdn.geocomply.com',
};

export function resolveGeoComplyBrowserGuardUrl(
    environment: KALSHI_ENVIRONMENT,
    configuredUrl: string | undefined,
): string {
    const trimmedUrl = configuredUrl?.trim();
    // `frontendEnv` is an unchecked cast of an env var, so an environment this
    // module doesn't know about lands here and misses the lookup. Fall back to
    // unconfigured rather than handing back an undefined typed as `string`.
    if (!trimmedUrl) return DEFAULT_GEOCOMPLY_BROWSER_GUARD_URLS[environment] ?? '';

    const trustedOrigin = GEOCOMPLY_BROWSER_GUARD_ORIGINS[environment];
    if (!trustedOrigin) return '';

    try {
        const parsedUrl = new URL(trimmedUrl);
        return parsedUrl.origin === trustedOrigin && !parsedUrl.username && !parsedUrl.password ? trimmedUrl : '';
    } catch {
        return '';
    }
}

const buildWsEndpointUrl = (baseUrl: string, pathname: string): string => new URL(pathname, baseUrl).toString();
const withoutTrailingSlash = (url: string): string => url.replace(/\/+$/, '');

const frontendEnv = (process.env.NEXT_PUBLIC_FRONTEND_ENVIRONMENT as KALSHI_ENVIRONMENT) || 'dev';
const exchangeWsBaseUrl = process.env.NEXT_PUBLIC_EXCHANGE_WS_URL || DEFAULT_EXCHANGE_WS_BASE_URL;
const geoComplyBrowserGuardUrl = resolveGeoComplyBrowserGuardUrl(
    frontendEnv,
    process.env.NEXT_PUBLIC_GEOCOMPLY_BROWSER_GUARD_URL,
);
/**
 * Deploy-time off switch for GeoComply location enforcement. Enforcement is
 * the default so a missing variable can never quietly drop a compliance
 * control; set it to `false` and the integration goes inert — no session, no
 * boundary, no request guard. Anything else (including a typo) leaves it on,
 * which is the safe direction to fail. It is a `NEXT_PUBLIC_` value, so it is
 * inlined at build time and flipping it needs a redeploy.
 *
 * A BrowserGuard URL is required too: enforcing without one would block every
 * geo-restricted trade with no way to clear it.
 */
const geoComplyEnabled =
    process.env.NEXT_PUBLIC_GEOCOMPLY_ENABLED?.trim().toLowerCase() !== 'false' && geoComplyBrowserGuardUrl.length > 0;

let baseUrl = 'https://kalshi.com';
switch (frontendEnv) {
    case 'demo':
        baseUrl = 'https://demo.kalshi.co';
        break;
    case 'dev':
        baseUrl = 'https://dev.kalshi.co';
        break;
    case 'lx':
        baseUrl = 'https://kalshi.co';
        break;
    case 'lxtest':
        baseUrl = 'https://lxtest.kalshi.co';
        break;
    case 'production':
        baseUrl = 'https://kalshi.com';
        break;
    case 'staging':
        baseUrl = 'https://staging.kalshi.dev';
        break;
}

if (typeof window !== 'undefined' && window.location.hostname === 'localhost') {
    baseUrl = 'http://localhost:3000';
}

const config: Config = {
    frontendEnv: frontendEnv,
    baseUrl: baseUrl,
    backendEnv: (process.env.NEXT_PUBLIC_BACKEND_ENVIRONMENT as KALSHI_ENVIRONMENT) || 'dev',
    exchangeApiUrl: process.env.NEXT_PUBLIC_EXCHANGE_API_URL || 'https://demo-api.kalshi.co',
    geoComplyBrowserGuardUrl,
    geoComplyEnabled,
    graphqlUrl:
        process.env.NEXT_PUBLIC_GRAPHQL_URL ||
        `${process.env.NEXT_PUBLIC_EXCHANGE_API_URL || 'https://demo-api.kalshi.co'}/graphql/v1/query`,
    statsApiUrl: process.env.NEXT_PUBLIC_STATS_API_URL || 'https://staging-api.stats.kalshi.co',
    exchangeWsUrl: buildWsEndpointUrl(exchangeWsBaseUrl, '/v1/ws'),
    marginWsUrl: buildWsEndpointUrl(exchangeWsBaseUrl, '/trade-api/ws/v2/margin'),
    kalshiPublicDocsS3BucketUrl:
        process.env.NEXT_PUBLIC_DOCS_S3_URL ||
        process.env.NEXT_PUBLIC_DOCS_URL ||
        'https://kalshi-public-docs.s3.amazonaws.com',
    kalshiPublicDocsCloudfrontUrl:
        process.env.NEXT_PUBLIC_DOCS_CDN_URL || process.env.NEXT_PUBLIC_DOCS_URL || 'https://assets.kalshi.com',
    kalshiImagesCloudfrontUrl: process.env.NEXT_PUBLIC_IMAGES_CLOUDFRONT_URL || 'https://d1lvyva3zy5u58.cloudfront.net',
    kalshiProductInfoCloudfrontUrl:
        (process.env.NEXT_PUBLIC_DOCS_CDN_URL || process.env.NEXT_PUBLIC_DOCS_URL || 'https://assets.kalshi.com') +
        '/product-info',
    kalshiPublicImagesS3BucketUrl: withoutTrailingSlash(
        process.env.NEXT_PUBLIC_PUBLIC_IMAGES_URL || 'https://kalshi-public-images.s3.us-east-2.amazonaws.com',
    ),
    kalshiProfilePreviewImagesS3BucketUrl:
        process.env.NEXT_PUBLIC_PROFILE_PREVIEW_IMAGES_URL ||
        'https://markets-x-bot-prod-preview-images.s3.us-east-1.amazonaws.com',
    kalshiSocialPublicS3BucketUrl: withoutTrailingSlash(
        process.env.NEXT_PUBLIC_SOCIAL_ASSETS_URL ||
            process.env.NEXT_PUBLIC_SOCIAL_PUBLIC_S3_BUCKET_URL ||
            'https://social-assets.kalshi.com',
    ),
    helpCenterUrl: 'https://help.kalshi.com',
    pollingInterval: Number(process.env.NEXT_PUBLIC_POLLING_INTERVAL) || 4000,
    fastPollingInterval: Number(process.env.NEXT_PUBLIC_FAST_POLLING_INTERVAL) || 5000,
    midPollingInterval: Number(process.env.NEXT_PUBLIC_MID_POLLING_INTERVAL) || 10000,
    slowPollingInterval: Number(process.env.NEXT_PUBLIC_SLOW_POLLING_INTERVAL) || 60000,
    // token expires 30 minutes after creation
    tokenExpiration: 1800000,
    maintenanceConfig: JSON.parse(process.env.NEXT_PUBLIC_MAINTENANCE_CONFIG || '{}'),
    tradingHoursConfig: JSON.parse(
        '{"Sunday":[{"open_time":"00:00:00","close_time":"00:00:00"}],"Monday":[{"open_time":"00:00:00","close_time":"00:00:00"}],"Tuesday":[{"open_time":"00:00:00","close_time":"00:00:00"}],"Wednesday":[{"open_time":"00:00:00","close_time":"00:00:00"}],"Thursday":[{"open_time":"00:00:00","close_time":"03:00:00"},{"open_time":"05:00:00","close_time":"00:00:00"}],"Friday":[{"open_time":"00:00:00","close_time":"00:00:00"}],"Saturday":[{"open_time":"00:00:00","close_time":"00:00:00"}]}',
    ),
    extraKycWait: Number(process.env.NEXT_PUBLIC_LX_EXTRA_KYC_WAIT) || 3000,
    kycAckWait: Number(process.env.NEXT_PUBLIC_LX_ACK_WAIT) || 3000,
    requestTimeoutMs: Number(process.env.NEXT_PUBLIC_REQUEST_TIMEOUT_MS) || 900000,
    withdrawalFee: 0,
    withdrawalFeeCard: 200, // in cents
    sentryDns: process.env.NEXT_PUBLIC_SENTRY_DNS || '',
    sentrySampleRate: (Number(process.env.NEXT_PUBLIC_SENTRY_SAMPLE_RATE) || 20) / 100,
    commitRef: process.env.NEXT_PUBLIC_COMMIT_REF || '',
    sentryRelease: process.env.NEXT_PUBLIC_SENTRY_RELEASE || '',
    sessionLength: 2590000,
    feSessionLength: 1800000,
    logRocketId: process.env.NEXT_PUBLIC_LOG_ROCKET_ID,
    manualBannerContent: process.env.NEXT_PUBLIC_MANUAL_BANNER_CONTENT ?? '',
    manualBannerVisibilityTokens: getArrayFromCSV(
        process.env.NEXT_PUBLIC_MANUAL_BANNER_VISIBILITY_TOKENS ?? '',
    ) as BannerVisibilityToken[],
    manualBannerPosition: (process.env.NEXT_PUBLIC_MANUAL_BANNER_POSITION || 'top') as BannerPosition,
    automatedBannerVisibilityTokens: getArrayFromCSV(
        process.env.NEXT_PUBLIC_AUTOMATED_BANNER_VISIBILITY_TOKENS ?? '',
    ) as BannerVisibilityToken[],
    socureSdkKey: process.env.NEXT_PUBLIC_SOCURE_SDK_KEY || '',
    socureInternationalSdkKey: process.env.NEXT_PUBLIC_SOCURE_INTERNATIONAL_SDK_KEY || '',
    // this means it will wait for 125 ms before launching next request therefore 8 req / second given next config
    minTimeBetweenRequestsMs: Number(process.env.NEXT_PUBLIC_MIN_TIME_BETWEEN_REQUESTS_MS) || 125,
    // this guarantees we make actually 8 requests per second just since
    // to convince your self just think that if the requests were resolved instantly it would take 8 * 125 === 1000ms
    // so actually this guarantees we do AT most 8 requests per second for each transportLayer method
    maxConcurrentRequests: Number(process.env.NEXT_PUBLIC_MAX_CONCURRENT_REQUESTS) || 8,
    logRocketSanitizeWords: getArrayFromCSV(
        process.env.NEXT_PUBLIC_LOG_ROCKET_SANITIZE_WORDS ?? 'password,ssn,code,token,session,api_key_id,private_key',
    ),
    logRocketUnauthenticatedSamplingPercentage: 0, // Number(process.env.NEXT_PUBLIC_LOG_ROCKET_UNAUTHENTICATED_SAMPLING_PERCENTAGE) || 1.0,
    apiDocsUrl: 'https://docs.kalshi.com',
    enableFixApiKeys: process.env.NEXT_PUBLIC_ENABLE_FIX_API_KEYS === 'true' || true,
    // Off by default; set NEXT_PUBLIC_AMERICAN_ODDS_ENABLED=true in Vercel + redeploy to re-enable.
    americanOddsEnabled: process.env.NEXT_PUBLIC_AMERICAN_ODDS_ENABLED === 'true',

    // Google
    googleMapsApiKey: process.env.NEXT_PUBLIC_GOOGLE_MAPS_API_KEY || '',
    googleOAuthClientId: process.env.NEXT_PUBLIC_GOOGLE_OAUTH_CLIENT_ID || '',

    // Apple
    appleOAuthClientId: process.env.NEXT_PUBLIC_APPLE_OAUTH_CLIENT_ID || '',

    // Facebook
    facebookShareMethod: (process.env.NEXT_PUBLIC_FACEBOOK_SHARE_METHOD as FACEBOOK_SHARE_METHOD) || 'share',
    facebookPixelId: process.env.NEXT_PUBLIC_FACEBOOK_PIXEL_ID || '',

    // Checkout
    checkoutPublicKey: process.env.NEXT_PUBLIC_CHECKOUT_PUBLIC_KEY || 'pk_sbox_dujmqfhxhhzsbqovxnhwxweh6ak',

    // Stripe
    stripePublishableKey: process.env.NEXT_PUBLIC_STRIPE_PUBLISHABLE_KEY || '',

    // Apple Pay (withdrawals via the Disbursement Request API use Kalshi's own
    // merchant id; deposits go through Stripe's). Empty hides the feature.
    applePayMerchantId: process.env.NEXT_PUBLIC_APPLE_PAY_MERCHANT_ID || '',

    // Sports Radar
    sportsRadarApiKey: process.env.NEXT_PUBLIC_SPORTS_RADAR_API_KEY || '',
    sportsRadarSessionReplayId: process.env.NEXT_PUBLIC_SPORTS_RADAR_SESSION_REPLAY_ID || '',
    sportradarWidgetClientId: process.env.NEXT_PUBLIC_SPORTRADAR_WIDGET_CLIENT_ID || '',

    // Aeropay
    aeropayConsumerId: process.env.NEXT_PUBLIC_AEROPAY_CONSUMER_ID || 'd0369da6-c415-45a6-936a-7d2ad0ddbbcd',
    aeropayConsumerIdDarkMode:
        process.env.NEXT_PUBLIC_AEROPAY_CONSUMER_ID_DARK_MODE || '91afc383-8520-46c0-b88d-823dbf87ec23',

    // WorldCoin
    wolrdCoinAppId: process.env.NEXT_PUBLIC_WORLDCOIN_APP_ID || '',

    // Mesh
    meshClientId: process.env.NEXT_PUBLIC_MESH_CLIENT_ID || 'c1600249-8f0b-4103-9340-08dd995cba71',

    // Braintree
    braintreeTokenizationKey: process.env.NEXT_PUBLIC_BRAINTREE_TOKENIZATION_KEY || '',

    // Social / GIFs
    giphyApiKey: process.env.NEXT_PUBLIC_GIPHY_API_KEY || '',

    // Internal tools
    internalApiKey:
        process.env.NEXT_INTERNAL_API_KEY || '2418aab67c804b2120313fa43a3f0600346bd1cfc1e8541d51eb34652bd65f57',

    // Statsig
    statsigEnvironment: process.env.NEXT_PUBLIC_STATSIG_ENVIRONMENT || 'development',

    // Riskified
    riskifiedDomain: process.env.NEXT_PUBLIC_RISKIFIED_DOMAIN || '',

    // Plaid Protect
    plaidProtectApiToken: process.env.NEXT_PUBLIC_PLAID_PROTECT_API_TOKEN || '',

    // Amplitude
    amplitudeApiKey: process.env.NEXT_PUBLIC_AMPLITUDE_API_KEY || '',

    // AWS WAF
    awsWafIntegrationUrl: process.env.NEXT_PUBLIC_AWS_WAF_INTEGRATION_URL || '',
    awsWafCookieDomainList: process.env.NEXT_PUBLIC_AWS_WAF_COOKIE_DOMAIN_LIST || '',
    awsWafFetchRetryCount: Number(process.env.NEXT_PUBLIC_AWS_WAF_FETCH_RETRY_COUNT) || 0,
    awsWafMaxTokenAgeMs: Number(process.env.NEXT_PUBLIC_AWS_WAF_MAX_TOKEN_AGE_MS) || 0,
};

export default config;
